import { toast } from "@/components/ui/sonner";
import {
  cascadeJurisdictionStatus,
  getAffectedJurisdictionsCount,
} from "@/hera-api/client/sdk.gen";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthQuerySetup } from "./use-auth-query-setup";

export interface JurisdictionCascadeHookProps {
  licenseTypeId?: string;
  enabled?: boolean;
}

export function useLicenseTypeJurisdictionCascade({
  licenseTypeId,
  enabled = true,
}: JurisdictionCascadeHookProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const queryClient = useQueryClient();

  // Get count of active configurations that would be affected
  const { data: activeConfigurationsCount, isLoading: isLoadingActiveCount } =
    useQuery({
      queryKey: [
        "affected-configurations-count",
        licenseTypeId,
        { include_deleted: false },
      ],
      queryFn: () =>
        getAffectedJurisdictionsCount({
          path: { license_type_id: licenseTypeId || "" },
          query: { include_deleted: false },
        }),
      enabled: isAuthSetupComplete && enabled && !!licenseTypeId,
    });

  // Get count of deleted configurations that would be affected
  const { data: deletedConfigurationsCount, isLoading: isLoadingDeletedCount } =
    useQuery({
      queryKey: [
        "affected-configurations-count",
        licenseTypeId,
        { include_deleted: true },
      ],
      queryFn: () =>
        getAffectedJurisdictionsCount({
          path: { license_type_id: licenseTypeId || "" },
          query: { include_deleted: true },
        }),
      enabled: isAuthSetupComplete && enabled && !!licenseTypeId,
    });

  // Calculate the count of deleted configurations only
  const deletedOnlyCount =
    (deletedConfigurationsCount?.data?.count || 0) -
    (activeConfigurationsCount?.data?.count || 0);

  // Mutation to cascade status changes
  const cascadeMutation = useMutation({
    mutationFn: async ({ isDeleted }: { isDeleted: boolean }) => {
      if (!licenseTypeId) {
        throw new Error("License type ID is required");
      }

      const response = await cascadeJurisdictionStatus({
        path: { license_type_id: licenseTypeId },
        body: { is_deleted: isDeleted },
      });

      if (response.error) {
        throw response.error;
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      const { isDeleted } = variables;
      const affectedCount = data?.affected_count || 0;

      if (affectedCount > 0) {
        toast({
          title: "Configurations Updated",
          description: `${affectedCount} license type configuration${affectedCount !== 1 ? "s" : ""} ${
            isDeleted ? "deactivated" : "reactivated"
          }.`,
        });
      } else {
        toast({
          title: "No Changes",
          description: "No configurations needed to be updated.",
        });
      }

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ["affected-configurations-count"],
      });
      queryClient.invalidateQueries({
        queryKey: ["jurisdictions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["license-types"],
      });
    },
    onError: (error) => {
      const errorMessage = getQueryErrorMessage(error);
      toast({
        title: "Failed to Update Jurisdictions",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  return {
    // Data
    activeConfigurationsCount: activeConfigurationsCount?.data?.count || 0,
    deletedConfigurationsCount: deletedOnlyCount,

    // Loading states
    isLoadingCounts: isLoadingActiveCount || isLoadingDeletedCount,
    isCascading: cascadeMutation.isPending,

    // Actions
    cascadeConfigurations: cascadeMutation.mutateAsync,

    // Error state
    cascadeError: cascadeMutation.error,
  };
}
